/**
 * The Resource class holds multiple types of information, including phone numbers, email, and web site addresses.
 *
 * The *type* of a Resource indicates the type of information, e.g. email, phone or www.
 * The *label* of a Resource is a descriptive label, like home or work.
 * The *value* of a Resource is the actual email, phone number or www address.
 *
 * Note that there can be multiple labels, including duplicate labels, for the same type of resource.
 *
 * For example, for a Contact we could have the following:
 *
 * ResourceId     Type     Label          Value
 * xxx-xxx-xxx    email    home           john@margaglione.com
 * xxx-xxx-xxx    email    home           john.margaglione@gmail.com
 * xxx-xxx-xxx    email    work           john@outlawpractice.com
 * xxx-xxx-xxx    email    work           john@margaglionelaw.com
 * xxx-xxx-xxx    phone    cell           512-569-1528
 * xxx-xxx-xxx    phone    Google Voice   512-522-9183
 * xxx-xxx-xxx    IM       Skype          jmargaglione
 * xxx-xxx-xxx    IM       WhatsApp       jmargaglione
 *
 * Note that Labels can have custom names, although we will always offer as many useful choices as possible.
 * In the user interface, we will probably offer icons in addition to option names when entering.
 *
 * This helper class is meant to help the UI find and display this information quickly and easily
 */

import IconButton from "../../ui/IconButton/IconButton";
import Icons, {IconType} from "../../ui/IconButton/Icons";
import {ReactElement} from 'react';
import store from '../../store/store';
import {maskEmail} from "../ContactFormat";
import upFirstLetter from "../../lib/picos/upFirstLetter";
import parsePhoneNumber from "libphonenumber-js";
import {formatPhoneNumber, formatPhoneNumberIntl, isPossiblePhoneNumber} from "react-phone-number-input";
import Resource, {TypeType} from "../../entities/resource";
import Participant from "../../entities/participant";
import Address from "../../entities/address";

export type LabelOption = {label: string, value: string, icon: IconType};

class ResourceHelper {
    // Safer email regex - simpler and more performant
    static emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Safer web regex - much simpler but still effective
    static webRegex = /^https?:\/\/[^\s/$.?#].[^\s]*$/i;

    static isValidEmail = (email: string) => email ? ResourceHelper.emailRegex.test(email) : false;
    static isValidPhone = (phone: string) => phone ? isPossiblePhoneNumber(phone) : false;
    static isValidWeb = (web: string) => web ? ResourceHelper.webRegex.test(web) : false;

    /**
     * Return an icon describing the type of resource: EMAIL, PHONE, WWW, OTHER.
     *
     * @param {Resource} resource
     * @returns {IconType}
     */
    static getResourceTypeIcon = (resource: Resource): IconType => {
        // Show the correct Icon next to the resource
        switch (resource.type) {
            case 'email':
                return Icons.EMAIL;
            case 'phone':
                return Icons.PHONE;
            case 'www':
                return Icons.WWW;
            default:
                return Icons.OTHER;
        }
    };

    /**
     * Return an icon showing the category for the type: personal, mobile, etc.
     *
     * @param {Resource} resource
     * @returns {IconType}
     */
    static getResourceLabelIcon = (resource: Resource | Address): IconType => {
        // Show the correct Icon next to the resource
        switch (resource.label) {
            case 'personal':
                return Icons.HOME;
            case 'mobile':
                return Icons.MOBILE;
            case 'fax':
                return Icons.FAX;
            case 'home':
                return Icons.HOME;
            case 'business':
                return Icons.BUSINESS;
            case 'other':
                return Icons.OTHER;
            default:
                return Icons.OTHER;
        }
    };

    /**
     * List of basic email labels
     */
    static getLabelOptions = (type: TypeType) => {
        let options = [];

        if (type === 'phone') options.push(
            {label: 'mobile', value: 'mobile', icon: Icons.MOBILE},
            {label: 'home', value: 'home', icon: Icons.HOME},
            {label: 'fax', value: 'fax', icon: Icons.FAX}
        );

        if (type === 'email') options.push({label: 'personal', value: 'personal', icon: Icons.HOME});

        return options.concat([
            {label: 'business', value: 'business', icon: Icons.BUSINESS},
            {label: 'other', value: 'other', icon: Icons.OTHER}
        ]);
    }

    static renderEmail(resource: Resource): ReactElement {
        const value = window.__demo_mode__ ? maskEmail(resource.value) : resource.value;
        const mailto = `mailto:${resource.value}`;
        const mail = resource.canContact ? <a href={mailto}>{value}</a> :
                     <div className="u-invalid">{value}</div>;
        const dnc = resource.canContact ? '' : 'DNC';

        return (
            <div key={`${resource.id}`}
                 className="ResourceLine"
            >
                <div className="ResourceButton"><IconButton
                    type={ResourceHelper.getResourceLabelIcon(resource)}
                    title={upFirstLetter(resource.label)}
                />
                </div>
                <div className={`ResourceText ${dnc}`}>{mail}</div>
                {resource.isPrimary &&
                 <div className="ResourceButton">
                     <IconButton type={Icons.STAR}
                                 title='Primary'
                     />
                 </div>}
            </div>
        );
    }

    static renderPhone(resource: Resource): ReactElement {
        const link = resource.label !== 'fax' ? 'dial:' : '';
        const dial = `${link}${resource.value}`;
        const phone = resource.canContact ? <a href={dial}>{resource.value}</a> :
                      <div className="DNC">{resource.value}</div>;

        return (
            <div key={`${resource.id}`}
                 className="ResourceLine"
            >
                <div className="ResourceButton"><IconButton
                    type={ResourceHelper.getResourceLabelIcon(resource)}
                    title={upFirstLetter(resource.label)}
                />
                </div>
                <div className="ResourceText">{phone}</div>
                {resource.isPrimary &&
                 <div className="ResourceButton">
                     <IconButton type={Icons.STAR} title='Primary'/>
                 </div>}
            </div>
        );
    }

    /**
     * Render a relationship as a string.
     *
     * @param {Participant} participant
     * @returns {string}
     */
    static renderRelationship = (participant: Participant): string => {
        // Need to look up the lookup names
        return participant.relationships.map(r => {
            const lookup = store.getState().lookups.find(l => l.id === r);
            return lookup ? lookup.key : 'Error';
        }).join(', ');
    };

    /**
     * Format a phone number as a string.
     *
     * @param {Resource} resource
     * @returns {string}
     */
    static formatPhoneNumber(resource: Resource): string {
        if (!resource) return '';

        // If this is a phone number, format accordingly
        const phoneNumber = parsePhoneNumber(resource.value, 'US');
        if (!phoneNumber) return 'INVALID: ' + resource.value;
        return phoneNumber.country === 'US' ? formatPhoneNumber(phoneNumber.number) : formatPhoneNumberIntl(phoneNumber.number);
    }

    /**
     * Check if a resource is valid
     * @param {'phone'|'email'|'web'} type
     * @param {string} value
     * @returns {boolean}
     */
    static isValid(type: TypeType, value: string): boolean {
        // Nulls are never valid
        if (!value) return false;

        if (type === 'email') return ResourceHelper.isValidEmail(value);
        else if (type === 'phone') return ResourceHelper.isValidPhone(value);
        else if (type === 'web') return ResourceHelper.isValidWeb(value);
    }
}

export default ResourceHelper;
